import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { getRenderer } from '../renderer/scene';
import { getGraphicalMapRuntimeScale } from './map-runtime-transform';
import type {
  GraphicalMapData,
  GraphicalMapMarker,
  GraphicalMapMeta,
  LoadedGraphicalMap,
  MapMarkerKind,
} from './map-asset-format';

const loadingManager = new THREE.LoadingManager();
const gltfLoader = new GLTFLoader(loadingManager);
const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
gltfLoader.setDRACOLoader(dracoLoader);

/** Lazily initialised — needs a renderer for detectSupport(). */
let ktx2LoaderInitialised = false;
let ktx2LoaderInstance: KTX2Loader | null = null;

/**
 * Attach a KTX2Loader to the shared GLTFLoader so that GLB files containing
 * KHR_texture_basisu (ETC1S / UASTC) textures can be decoded at runtime.
 * Must be called after the WebGL renderer is available.
 */
function ensureKTX2Loader(): void {
  if (ktx2LoaderInitialised) return;
  try {
    const renderer: THREE.WebGLRenderer = getRenderer();
    const ktx2Loader = new KTX2Loader();
    ktx2Loader.setTranscoderPath('/libs/basis/');
    ktx2Loader.detectSupport(renderer);
    gltfLoader.setKTX2Loader(ktx2Loader);
    ktx2LoaderInstance = ktx2Loader;
    ktx2LoaderInitialised = true;
  } catch {
    // Renderer not yet available — will try again on next load call.
  }
}

/** Shared GLTF/DRACO loader for all map-related asset loading. */
export function getMapGltfLoader(): GLTFLoader {
  return gltfLoader;
}

let currentMapData: GraphicalMapData | null = null;

const _tmpWorldPosition = new THREE.Vector3();
const _tmpWorldScale = new THREE.Vector3();
const _tmpWorldQuaternion = new THREE.Quaternion();
const _tmpWorldEuler = new THREE.Euler();

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === 'true') return true;
    if (value === 'false') return false;
  }
  return undefined;
}

function normalizeKind(value: string): MapMarkerKind | string {
  const normalized = value.toLowerCase().replace(/[\s_]+/g, '-');
  switch (normalized) {
    case 'spawn':
      return 'spawn';
    case 'bombsite':
    case 'bomb-site':
      return 'bombsite';
    case 'buy':
    case 'buyzone':
    case 'buy-zone':
      return 'buyzone';
    case 'sound':
    case 'soundscape':
      return 'soundscape';
    case 'lightprobe':
    case 'light-probe':
      return 'lightprobe';
    case 'visibility-zone':
    case 'visibility':
    case 'viszone':
    case 'vis-zone':
      return 'visibility-zone';
    case 'ladder':
      return 'ladder';
    case 'door':
      return 'door';
    case 'prop':
      return 'prop';
    default:
      return normalized || 'unknown';
  }
}

function inferKindFromName(name: string): MapMarkerKind | string {
  const normalized = name.toLowerCase();
  if (normalized.startsWith('spawn_')) return 'spawn';
  if (normalized.startsWith('bombsite_')) return 'bombsite';
  if (normalized.startsWith('buyzone_') || normalized.startsWith('buy_')) return 'buyzone';
  if (normalized.startsWith('soundscape_')) return 'soundscape';
  if (normalized.startsWith('lightprobe_')) return 'lightprobe';
  if (normalized.startsWith('viszone_') || normalized.startsWith('visibility_')) return 'visibility-zone';
  if (normalized.startsWith('ladder_')) return 'ladder';
  if (normalized.startsWith('door_')) return 'door';
  if (normalized.startsWith('prop_')) return 'prop';
  return 'unknown';
}

function inferTeam(name: string): 't' | 'ct' | 'dm' | undefined {
  const normalized = name.toLowerCase();
  if (normalized.startsWith('spawn_t_')) return 't';
  if (normalized.startsWith('spawn_ct_')) return 'ct';
  if (normalized.startsWith('spawn_dm_')) return 'dm';
  return undefined;
}

function toTuple3(vec: THREE.Vector3 | THREE.Euler): [number, number, number] {
  return [vec.x, vec.y, vec.z];
}

function cloneMarker(marker: GraphicalMapMarker): GraphicalMapMarker {
  return {
    name: marker.name,
    kind: marker.kind,
    position: [...marker.position],
    rotation: [...marker.rotation],
    scale: [...marker.scale],
    data: { ...marker.data },
  };
}

function cloneMapData(data: GraphicalMapData): GraphicalMapData {
  return {
    meta: { ...data.meta },
    markers: data.markers.map(cloneMarker),
    spawns: data.spawns.map(cloneMarker),
    bombsites: data.bombsites.map(cloneMarker),
    buyZones: data.buyZones.map(cloneMarker),
    soundscapes: data.soundscapes.map(cloneMarker),
    props: data.props.map(cloneMarker),
    ladders: data.ladders.map(cloneMarker),
    doors: data.doors.map(cloneMarker),
  };
}

function extractMarkerData(obj: THREE.Object3D): Record<string, unknown> {
  const data: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj.userData ?? {})) {
    if (key === 'gltfExtensions') continue;
    if (value === undefined) continue;
    data[key] = value;
  }
  return data;
}

function extractMapMeta(root: THREE.Group, assetPath: string): GraphicalMapMeta {
  const fallbackId = assetPath.split('/').filter(Boolean).at(-2) ?? 'graphical-map';
  const meta: GraphicalMapMeta = {
    id: fallbackId,
    name: root.name || fallbackId,
    author: 'Unknown',
    version: 1,
    arenaHalf: 80,
    scenePath: assetPath,
  };

  const applyMeta = (data: Record<string, unknown>): void => {
    meta.id = readString(data.cp_map_id) ?? meta.id;
    meta.name = readString(data.cp_map_name) ?? meta.name;
    meta.author = readString(data.cp_author) ?? meta.author;
    meta.version = readNumber(data.cp_version) ?? meta.version;
    meta.arenaHalf = readNumber(data.cp_arena_half) ?? meta.arenaHalf;
  };

  applyMeta(root.userData ?? {});

  root.traverse((obj) => {
    if (readBoolean(obj.userData?.cp_map_root) === true) {
      applyMeta(obj.userData as Record<string, unknown>);
    }
  });

  return meta;
}

function extractGraphicalMapData(root: THREE.Group, assetPath: string): GraphicalMapData {
  const markers: GraphicalMapMarker[] = [];

  root.traverse((obj) => {
    const rawKind = readString(obj.userData?.cp_kind);
    const kind = rawKind ? normalizeKind(rawKind) : inferKindFromName(obj.name);
    if (kind === 'unknown') return;

    const data = extractMarkerData(obj);
    if (kind === 'spawn' && !data.cp_team) {
      data.cp_team = inferTeam(obj.name);
    }

    markers.push({
      name: obj.name || `${kind}-${markers.length + 1}`,
      kind,
      position: toTuple3(obj.getWorldPosition(_tmpWorldPosition)),
      rotation: toTuple3(_tmpWorldEuler.setFromQuaternion(obj.getWorldQuaternion(_tmpWorldQuaternion), 'XYZ')),
      scale: toTuple3(obj.getWorldScale(_tmpWorldScale)),
      data,
    });
  });

  const meta = extractMapMeta(root, assetPath);
  return {
    meta,
    markers,
    spawns: markers.filter((marker) => marker.kind === 'spawn'),
    bombsites: markers.filter((marker) => marker.kind === 'bombsite'),
    buyZones: markers.filter((marker) => marker.kind === 'buyzone'),
    soundscapes: markers.filter((marker) => marker.kind === 'soundscape'),
    props: markers.filter((marker) => marker.kind === 'prop'),
    ladders: markers.filter((marker) => marker.kind === 'ladder'),
    doors: markers.filter((marker) => marker.kind === 'door'),
  };
}

function prepareScene(root: THREE.Group): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    obj.castShadow = true;
    obj.receiveShadow = true;

    const shouldRender = readBoolean(obj.userData?.cp_render);
    const occluder = readBoolean(obj.userData?.cp_occluder) === true;
    const collisionOnly = readBoolean(obj.userData?.cp_collision_only) === true;
    if (shouldRender === false || collisionOnly || occluder) {
      obj.visible = false;
    }
  });
}

/**
 * F-MAP-011: Force `DoubleSide` rendering on every mesh under a Z-mirrored
 * root. The mirror reverses the world-matrix determinant which flips
 * triangle winding; Three.js handles backface culling for negative-scale
 * matrices, but the shadow pass + SSR derive normals from the unflipped
 * mesh, leaving interior walls inconsistently lit. DoubleSide eliminates
 * the visible artefact at the cost of one extra fragment shader pass per
 * triangle — acceptable for the outpost-only roots that ship today.
 */
function forceDoubleSidedMaterials(root: THREE.Group): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    if (Array.isArray(obj.material)) {
      for (const m of obj.material) m.side = THREE.DoubleSide;
    } else if (obj.material) {
      obj.material.side = THREE.DoubleSide;
    }
  });
}

function applyRuntimeScaleToMarker(
  marker: GraphicalMapMarker,
  scale: [number, number, number],
): GraphicalMapMarker {
  // Mirror POSITION only. Rotations are left in raw GLB (Blender-authored)
  // space: decomposing a world matrix that embeds a negative-scale flip
  // produces ambiguous Euler angles, and the Blender-authored Euler values
  // are the ones consumers (interactive-elements, door yaw) actually want.
  return {
    ...marker,
    position: [
      marker.position[0] * scale[0],
      marker.position[1] * scale[1],
      marker.position[2] * scale[2],
    ],
    scale: [...marker.scale],
    rotation: [...marker.rotation],
    data: { ...marker.data },
  };
}

function applyRuntimeScaleToMapData(
  data: GraphicalMapData,
  scale: [number, number, number],
): GraphicalMapData {
  const transform = (m: GraphicalMapMarker): GraphicalMapMarker =>
    applyRuntimeScaleToMarker(m, scale);
  const transformed: GraphicalMapData = {
    meta: { ...data.meta },
    markers: data.markers.map(transform),
    spawns: data.spawns.map(transform),
    bombsites: data.bombsites.map(transform),
    buyZones: data.buyZones.map(transform),
    soundscapes: data.soundscapes.map(transform),
    props: data.props.map(transform),
    ladders: data.ladders.map(transform),
    doors: data.doors.map(transform),
  };
  return transformed;
}

export async function loadGraphicalMap(assetPath: string): Promise<LoadedGraphicalMap> {
  ensureKTX2Loader();
  const gltf = await gltfLoader.loadAsync(assetPath);
  const root = gltf.scene;
  prepareScene(root);

  const mapId = assetPath.split('/').filter(Boolean).at(-2) ?? 'graphical-map';

  // Extract markers BEFORE applying the runtime scale so door/ladder
  // rotations stay in Blender-authored Euler space (a negative-scale flip
  // would otherwise make the decomposed rotation ambiguous). Positions get
  // mirrored in a second pass.
  root.updateMatrixWorld(true);
  const rawData = extractGraphicalMapData(root, assetPath);

  const [sx, sy, sz] = getGraphicalMapRuntimeScale(mapId);
  root.scale.set(sx, sy, sz);
  root.updateMatrixWorld(true);

  // F-MAP-011: A negative axis on `root.scale` flips the world-matrix
  // determinant and reverses triangle winding. Three.js handles GL-level
  // culling for negative-scale matrices, but the shadow pass and SSR derive
  // normals from the unflipped geometry, causing inconsistent shadowing on
  // interior surfaces. Force DoubleSide on the affected map root so both
  // pass-throughs render correctly.
  if (sx < 0 || sy < 0 || sz < 0) {
    forceDoubleSidedMaterials(root);
  }

  const data = applyRuntimeScaleToMapData(rawData, [sx, sy, sz]);

  currentMapData = cloneMapData(data);
  root.userData.graphicalMap = data.meta;

  return {
    root,
    data,
  };
}

export function getCurrentGraphicalMapData(): GraphicalMapData | null {
  return currentMapData ? cloneMapData(currentMapData) : null;
}

export function disposeMapAssetState(): void {
  currentMapData = null;

  // Release the worker pools owned by DRACO and KTX2 loaders. These module-
  // level singletons spawn Workers that otherwise persist across SPA
  // navigation and hold on to transcoder WASM memory.
  try { dracoLoader.dispose(); } catch { /* already disposed */ }
  if (ktx2LoaderInstance) {
    try { ktx2LoaderInstance.dispose(); } catch { /* already disposed */ }
    ktx2LoaderInstance = null;
  }
  ktx2LoaderInitialised = false;
}
